import axios from "axios"
import {
  AmbienteDoUsuarioResposta,
  TokensKeycloakResposta,
} from "@/repositorios/autenticacao/autenticacao.modelo"
import { concluirSessaoAutenticada } from "@/servicos/autenticacao.service"
import SessaoServico from "@/servicos/sessao.service"

interface TransacaoKeycloak {
  state: string
  realm: string
  redirectUri: string
  rotaRetorno: string
  criadaEm: number
}

const CHAVE_TRANSACAO = "_keycloak_auth_transaction"
const CHAVE_ERRO = "_keycloak_auth_error"
const TEMPO_MAXIMO_TRANSACAO_MS = 10 * 60 * 1000
const CLIENT_ID = "pontotel"

function validarRealm(realm: string | null | undefined): string {
  if (!realm || !/^[a-zA-Z0-9._-]+$/.test(realm)) {
    throw new Error("Realm do Keycloak inválido")
  }

  return realm
}

function gerarValorAleatorio(tamanho = 32): string {
  const bytes = new Uint8Array(tamanho)
  window.crypto.getRandomValues(bytes)

  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")
}

export function construirRedirectUriKeycloak(realm: string): string {
  const redirectUri = new URL(window.location.pathname, window.location.origin)
  redirectUri.searchParams.set("realm", realm)
  return redirectUri.toString()
}

function extrairBaseUrlKeycloak(urlDeAutenticacao: URL): string {
  const indiceRealm = urlDeAutenticacao.pathname.indexOf("/realms/")
  if (indiceRealm < 0) {
    throw new Error("URL de autenticação do Keycloak inválida")
  }

  const prefixo = urlDeAutenticacao.pathname.slice(0, indiceRealm)
  return `${urlDeAutenticacao.origin}${prefixo}`.replace(/\/$/, "")
}

function obterBaseUrlKeycloakConfiavel(): string {
  const baseUrlConfigurada = process.env.VUE_APP_KEYCLOAK_BASE_URL
  if (!baseUrlConfigurada) {
    throw new Error("URL base do Keycloak não configurada")
  }

  const baseUrl = new URL(baseUrlConfigurada)
  if (baseUrl.protocol !== "https:") {
    throw new Error("Protocolo da URL base do Keycloak não permitido")
  }

  return baseUrl.toString().replace(/\/$/, "")
}

function validarUrlDeAutenticacao(
  urlDeAutenticacao: URL,
  realm: string,
  baseUrlConfiavel: string
): void {
  const ehHostInterno = urlDeAutenticacao.hostname.endsWith(".cluster.local")
  const possuiProtocoloDuplicado =
    ["http", "https"].includes(urlDeAutenticacao.hostname) ||
    urlDeAutenticacao.pathname.startsWith("//")
  if (ehHostInterno || possuiProtocoloDuplicado) {
    throw new Error("URL de autenticação do Keycloak inválida")
  }

  if (urlDeAutenticacao.protocol !== "https:") {
    throw new Error("Protocolo da URL de autenticação não permitido")
  }

  if (!urlDeAutenticacao.pathname.includes(`/realms/${realm}/`)) {
    throw new Error("Realm divergente na URL de autenticação")
  }

  if (extrairBaseUrlKeycloak(urlDeAutenticacao) !== baseUrlConfiavel) {
    throw new Error("Origem de autenticação divergente da configuração")
  }
}

function salvarTransacao(transacao: TransacaoKeycloak): void {
  sessionStorage.setItem(CHAVE_TRANSACAO, JSON.stringify(transacao))
}

function obterTransacao(): TransacaoKeycloak | null {
  const transacaoSalva = sessionStorage.getItem(CHAVE_TRANSACAO)
  if (!transacaoSalva) {
    return null
  }

  try {
    return JSON.parse(transacaoSalva) as TransacaoKeycloak
  } catch {
    return null
  }
}

function limparParametrosDoCallback(rota: string): void {
  const rotaNormalizada = rota.startsWith("/") ? rota : `/${rota}`
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}#${rotaNormalizada}`
  )
}

export function possuiCallbackKeycloak(): boolean {
  const parametros = new URLSearchParams(window.location.search)
  const transacao = obterTransacao()
  return !!transacao && (parametros.has("code") || parametros.has("error"))
}

export async function iniciarLoginKeycloak(
  ambiente: AmbienteDoUsuarioResposta,
  rotaRetorno = "/"
): Promise<void> {
  const realm = validarRealm(ambiente.keycloak_realm)
  if (!ambiente.url_de_autenticacao) {
    throw new Error("Ambiente sem URL de autenticação")
  }

  const urlDeAutenticacao = new URL(ambiente.url_de_autenticacao)
  validarUrlDeAutenticacao(
    urlDeAutenticacao,
    realm,
    obterBaseUrlKeycloakConfiavel()
  )
  const redirectUri = construirRedirectUriKeycloak(realm)
  const state = gerarValorAleatorio()

  urlDeAutenticacao.searchParams.set("redirect_uri", redirectUri)
  urlDeAutenticacao.searchParams.set("state", state)

  salvarTransacao({
    state,
    realm,
    redirectUri,
    rotaRetorno,
    criadaEm: Date.now(),
  })

  window.location.assign(urlDeAutenticacao.toString())
}

export async function processarCallbackKeycloak(): Promise<boolean> {
  if (!possuiCallbackKeycloak()) {
    return false
  }

  const parametros = new URLSearchParams(window.location.search)
  const transacao = obterTransacao()

  try {
    const code = parametros.get("code")
    const state = parametros.get("state")
    const sessionState = parametros.get("session_state")
    const realm = validarRealm(parametros.get("realm") || undefined)

    if (
      !transacao ||
      !state ||
      state !== transacao.state ||
      realm !== transacao.realm ||
      Date.now() - transacao.criadaEm > TEMPO_MAXIMO_TRANSACAO_MS
    ) {
      throw new Error("Callback do Keycloak inválido ou expirado")
    }

    if (parametros.has("error")) {
      throw new Error("Autenticação cancelada ou recusada pelo Keycloak")
    }

    if (!code || !sessionState) {
      throw new Error("Callback do Keycloak inválido ou expirado")
    }

    limparParametrosDoCallback(transacao.rotaRetorno)

    const corpo = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: CLIENT_ID,
      code,
      redirect_uri: transacao.redirectUri,
    })
    corpo.set("session_state", sessionState)
    const baseUrlKeycloak = obterBaseUrlKeycloakConfiavel()
    const urlRenovacaoTokenKeycloak = construirUrlDeTokenKeycloak(
      baseUrlKeycloak,
      realm
    )
    const urlLogoutKeycloak = construirUrlDeLogoutKeycloakParaSessao(
      baseUrlKeycloak,
      realm
    )
    const resposta = (await axios.post(urlRenovacaoTokenKeycloak, corpo, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })) as unknown as { data: TokensKeycloakResposta }

    await concluirSessaoAutenticada(
      resposta.data.access_token,
      resposta.data.refresh_token,
      {
        urlRenovacaoTokenKeycloak,
        urlLogoutKeycloak,
        realmKeycloak: realm,
      }
    )

    sessionStorage.removeItem(CHAVE_TRANSACAO)
    sessionStorage.removeItem(CHAVE_ERRO)
    return true
  } catch (erro) {
    const mensagemDeErro =
      (erro as { codigo?: number })?.codigo === 403
        ? "usuarioSemAcessoAoTimesheet"
        : "erroAoAutenticarComKeycloak"
    SessaoServico.limparInformacoesDaSessao()
    sessionStorage.removeItem(CHAVE_TRANSACAO)
    sessionStorage.setItem(CHAVE_ERRO, mensagemDeErro)
    limparParametrosDoCallback("/auth")
    return false
  }
}

export function consumirErroKeycloak(): string | null {
  const erro = sessionStorage.getItem(CHAVE_ERRO)
  sessionStorage.removeItem(CHAVE_ERRO)
  return erro
}

export function construirUrlDeTokenKeycloak(
  baseUrl: string,
  realm: string
): string {
  return `${baseUrl.replace(/\/$/, "")}/realms/${encodeURIComponent(
    validarRealm(realm)
  )}/protocol/openid-connect/token`
}

function construirUrlDeLogoutKeycloakParaSessao(
  baseUrl: string,
  realm: string
): string {
  const url = new URL(
    `${baseUrl.replace(/\/$/, "")}/realms/${encodeURIComponent(
      validarRealm(realm)
    )}/protocol/openid-connect/logout`
  )
  url.searchParams.set("client_id", CLIENT_ID)
  url.searchParams.set(
    "post_logout_redirect_uri",
    construirPostLogoutRedirectUri(
      window.location.origin,
      window.location.pathname
    )
  )

  return url.toString()
}

export function construirPostLogoutRedirectUri(
  origem: string,
  caminho: string
): string {
  return `${origem}${caminho}#/auth`
}
